import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch, HiShoppingCart, HiExclamation } from 'react-icons/hi';
import { productsAPI } from '../services/api';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { sourceLabel } from '../utils/sourceLabel';
import { calcularEconomia, dinero } from '../utils/economia';
import ModalShell from './ModalShell';

/**
 * La ficha de un producto. Una sola, para el candidato y para el ya importado.
 *
 * Antes eran dos ventanas con el mismo título y distinto contenido, y la del
 * producto importado enseñaba menos que la del candidato: después de importar
 * sabías menos que antes. Ahora las dos piden la misma ficha al proveedor y
 * comparten esqueleto; lo único que cambia es que un producto tuyo añade su
 * bloque de catálogo, y que su precio está fijado en vez de estimado.
 */

function Dato({ etiqueta, children }) {
    return (
        <div className="flex justify-between items-baseline gap-4 text-sm">
            <span className="text-slate-500 dark:text-slate-400">{etiqueta}</span>
            <span className="font-medium text-slate-900 dark:text-white text-right">{children}</span>
        </div>
    );
}

export default function ProductDetailsModal({ vista, onImport, importing = false, onClose, comision = null }) {
    const { t } = useTranslation();
    const [detalle, setDetalle] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [imagenActiva, setImagenActiva] = useState(0);
    // Que se vende exactamente. eBay publica un articulo concreto, no un
    // producto con quince variantes.
    const [vidElegido, setVidElegido] = useState(null);

    const { fuente, externalId, almacen } = vista;

    useEffect(() => {
        let vigente = true;
        setCargando(true);

        productsAPI
            .getDetail({ source: fuente, externalId, ...(almacen ? { warehouse: almacen } : {}) })
            .then((res) => {
                if (!vigente) return;
                setDetalle(res.data.data);
                // Se preselecciona la mas barata con existencias, la misma
                // regla que aplica el servidor si nadie elige.
                const variantes = res.data.data?.variantes || [];
                const conPrecio = variantes.filter((v) => v.vid && v.precio > 0);
                const conStock = conPrecio.filter((v) => (v.existencias || []).some((e) => e.cantidad > 0));
                const candidatas = conStock.length > 0 ? conStock : conPrecio;
                const porDefecto = [...candidatas].sort((a, b) => a.precio - b.precio)[0];
                setVidElegido(vista.crudo?.variant_id || porDefecto?.vid || null);
            })
            .catch(() => {
                if (vigente) setDetalle({ detalleDisponible: false, motivo: 'ERROR_PROVEEDOR' });
            })
            .finally(() => {
                if (vigente) setCargando(false);
            });

        return () => {
            vigente = false;
        };
    }, [fuente, externalId, almacen]);

    const imagenes = useMemo(() => {
        const delDetalle = detalle?.imagenes || [];
        const todas = delDetalle.length > 0 ? delDetalle : vista.imagenes;
        return todas.length > 0 ? todas : [PRODUCT_IMAGE_FALLBACK];
    }, [detalle, vista.imagenes]);

    const existencias = useMemo(() => {
        const porPais = new Map();
        for (const v of detalle?.variantes || []) {
            for (const e of v.existencias || []) {
                porPais.set(e.pais, (porPais.get(e.pais) || 0) + e.cantidad);
            }
        }
        return [...porPais.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
    }, [detalle]);

    // Tarifa de hoy, la que devuelve el proveedor ahora mismo.
    const tarifaDeHoy = detalle?.envio?.opciones?.[0] || null;

    // Un producto tuyo enseña lo que tiene guardado, que es la verdad de tu
    // catálogo. Un candidato se estima con la tarifa de hoy.
    const esImportado = vista.origen === 'importado';
    const envioAplicado = esImportado ? vista.envioGuardado : (tarifaDeHoy ? { costo: tarifaDeHoy.precio, transportista: tarifaDeHoy.transportista, plazo: tarifaDeHoy.plazo, destino: detalle?.envio?.destino } : null);

    const varianteElegida = (detalle?.variantes || []).find((v) => v.vid === vidElegido) || null;

    const cuenta = calcularEconomia({
        // Lo que se paga es el precio de la variante que se va a vender.
        costoProducto: esImportado ? vista.costoProducto : (varianteElegida?.precio ?? vista.costoProducto),
        costoEnvio: envioAplicado?.costo ?? null,
        margen: vista.margenPct,
        precioVenta: vista.precioFijado,
        // Lo que se queda el destino. Sin él, la ficha prometía una ganancia
        // que la comisión se llevaba en parte.
        comision
    });

    // Si el producto es tuyo y no tiene envío guardado, la tarifa de hoy se
    // enseña aparte, como referencia. Meterla en la ganancia contradiría lo que
    // dice su tarjeta, que es exactamente el defecto que veníamos de arreglar.
    const referenciaDeHoy = esImportado && !vista.envioGuardado && tarifaDeHoy ? tarifaDeHoy : null;

    const motivoSinFicha = detalle && detalle.detalleDisponible === false ? detalle.motivo : null;
    const fecha = (v) => (v ? new Date(v).toLocaleDateString() : '—');

    return (
        <ModalShell
            title={t('product_details.title')}
            onClose={onClose}
            footer={
                vista.origen === 'candidato' ? (
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => onImport(vista.crudo, vidElegido)}
                            disabled={importing}
                            className="btn-primary flex-1 disabled:opacity-60"
                        >
                            <HiShoppingCart className="w-4 h-4 mr-2" />
                            {t('product_search.import')}
                        </button>
                        <button type="button" onClick={onClose} className="btn-secondary flex-1">
                            {t('common.close')}
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={onClose} className="btn-secondary w-full">
                        {t('common.close')}
                    </button>
                )
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Galería */}
                <div className="space-y-3">
                    <div className="aspect-square bg-slate-100 dark:bg-slate-700 rounded-xl overflow-hidden">
                        <img
                            src={imagenes[imagenActiva] || PRODUCT_IMAGE_FALLBACK}
                            alt={vista.titulo}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {imagenes.length > 1 && (
                        <div className="grid grid-cols-5 gap-2">
                            {imagenes.slice(0, 10).map((img, idx) => (
                                <button
                                    key={img}
                                    type="button"
                                    onClick={() => setImagenActiva(idx)}
                                    aria-label={t('product_details.image_alt', { n: idx + 1 })}
                                    aria-current={idx === imagenActiva}
                                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                                        idx === imagenActiva
                                            ? 'border-primary-500'
                                            : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                                    }`}
                                >
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    {/* Identidad */}
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-snug">
                            {vista.titulo}
                        </h3>
                        <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-slate-500 dark:text-slate-400">
                            {vista.catalogo && (
                                <span className={`badge ${vista.catalogo.publicado ? 'badge-success' : 'badge-warning'}`}>
                                    {vista.catalogo.publicado
                                        ? t('imported_products.status_published')
                                        : t('imported_products.status_draft')}
                                </span>
                            )}
                            <span>{sourceLabel(vista.fuente)}</span>
                            {detalle?.categoria && <span>· {detalle.categoria}</span>}
                        </div>
                    </div>

                    {/* Las cuentas */}
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                        <Dato etiqueta={t('imported_products.cost')}>{dinero(cuenta.costoProducto)}</Dato>

                        <Dato etiqueta={t('product_details.shipping_to', { country: envioAplicado?.destino || detalle?.envio?.destino || 'US' })}>
                            {cargando && !esImportado ? (
                                <span className="text-slate-400">{t('product_details.calculating')}</span>
                            ) : envioAplicado ? (
                                <>
                                    {dinero(envioAplicado.costo)}
                                    {(envioAplicado.transportista || envioAplicado.plazo) && (
                                        <span className="block text-xs font-normal text-slate-500">
                                            {[envioAplicado.transportista, envioAplicado.plazo].filter(Boolean).join(' · ')}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="text-slate-400">{t('product_details.shipping_unknown')}</span>
                            )}
                        </Dato>

                        <Dato etiqueta={vista.precioFijado !== null ? t('product_details.selling_price') : t('product_details.estimated_price')}>
                            {dinero(cuenta.precioVenta)}
                        </Dato>

                        <div className="pt-2 border-t border-slate-200 dark:border-slate-600 flex justify-between items-baseline gap-4">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {cuenta.gananciaCompleta
                                    ? t('product_details.real_profit')
                                    : t('product_details.profit_before_shipping')}
                            </span>
                            <span className={`text-xl font-bold ${cuenta.ganancia > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {dinero(cuenta.ganancia)}
                            </span>
                        </div>

                        <p className="text-[11px] text-slate-400 leading-snug">
                            {vista.precioFijado === null && t('product_search.margin_note', { margin: vista.margenPct })}
                            {detalle?.envio?.varianteNombre && !esImportado && (
                                <> {t('product_details.shipping_variant_note', { variant: detalle.envio.varianteNombre })}</>
                            )}
                            {vista.envioGuardado?.fecha && (
                                <> {t('product_details.shipping_quoted_on', { date: fecha(vista.envioGuardado.fecha) })}</>
                            )}
                        </p>

                        {referenciaDeHoy && (
                            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-snug">
                                {t('product_details.shipping_today_hint', {
                                    price: dinero(referenciaDeHoy.precio),
                                    carrier: referenciaDeHoy.transportista
                                })}
                            </p>
                        )}
                    </div>

                    {/* Lo que declara el proveedor. Cada fila solo si hay dato. */}
                    <div className="space-y-2">
                        {(vista.crudo?.variant_name || varianteElegida?.nombre) && (
                            <Dato etiqueta={t('product_details.selected_variant')}>
                                {esImportado ? vista.crudo.variant_name : varianteElegida?.nombre}
                            </Dato>
                        )}
                        {vista.almacen && <Dato etiqueta={t('product_details.warehouse')}>{vista.almacen}</Dato>}
                        {detalle?.pesoGramos > 0 && (
                            <Dato etiqueta={t('product_details.weight')}>{detalle.pesoGramos} g</Dato>
                        )}
                        {detalle?.rangoPrecio && detalle.rangoPrecio.max > detalle.rangoPrecio.min && (
                            <Dato etiqueta={t('product_details.price_range')}>
                                {dinero(detalle.rangoPrecio.min)} – {dinero(detalle.rangoPrecio.max)}
                            </Dato>
                        )}
                        {existencias.length > 0 && (
                            <Dato etiqueta={t('product_details.stock')}>
                                {existencias.map(([pais, n]) => `${pais} ${n.toLocaleString()}`).join(' · ')}
                            </Dato>
                        )}
                        {detalle?.listados > 0 && (
                            <Dato etiqueta={t('product_details.listed_by')}>{detalle.listados.toLocaleString()}</Dato>
                        )}
                        {detalle?.condicion && (
                            <Dato etiqueta={t('product_details.condition')}>{detalle.condicion}</Dato>
                        )}
                        {detalle?.vendedor && (
                            <Dato etiqueta={t('product_details.seller')}>
                                {detalle.vendedor.nombre}
                                {detalle.vendedor.valoracion && (
                                    <span className="block text-xs font-normal text-slate-500">
                                        {detalle.vendedor.valoracion}% · {detalle.vendedor.votos}
                                    </span>
                                )}
                            </Dato>
                        )}
                    </div>

                    {/* Bloque de catálogo: solo si el producto ya es tuyo. */}
                    {vista.catalogo && (
                        <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4 space-y-2">
                            <p className="text-xs text-slate-500">{t('product_details.connected_store')}</p>
                            <p className="font-semibold text-slate-900 dark:text-white">
                                {vista.catalogo.tienda || '—'}
                            </p>
                            <p className="text-sm text-slate-500 capitalize">{vista.catalogo.plataforma}</p>

                            {vista.catalogo.listingId && (
                                <p className="text-xs text-slate-500">
                                    {t('imported_products.listing_id')}:{' '}
                                    <span className="font-mono text-slate-700 dark:text-slate-300">
                                        {vista.catalogo.listingId}
                                    </span>
                                </p>
                            )}

                            <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
                                <div>
                                    <p className="text-slate-500">{t('product_details.created')}</p>
                                    <p className="text-slate-900 dark:text-white font-medium">
                                        {fecha(vista.catalogo.creado)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t('product_details.updated')}</p>
                                    <p className="text-slate-900 dark:text-white font-medium">
                                        {fecha(vista.catalogo.actualizado)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {vista.urlProveedor && (
                        <a
                            href={vista.urlProveedor}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary btn-sm inline-flex items-center"
                        >
                            <HiSearch className="w-4 h-4 mr-1" />
                            {t('product_search.view_on', { source: sourceLabel(vista.fuente) })}
                        </a>
                    )}
                </div>
            </div>

            {cargando && (
                <div className="space-y-2" aria-live="polite">
                    <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                    <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                </div>
            )}

            {/* Si el proveedor no da ficha se dice, con su motivo. Rellenar el
                hueco con lo que ya se sabía sería fingir que hay detalle. */}
            {!cargando && motivoSinFicha && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 p-4">
                    <HiExclamation className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        {t(`product_details.motivo_${motivoSinFicha}`, t('product_details.motivo_ERROR_PROVEEDOR'))}
                    </p>
                </div>
            )}

            {detalle?.variantes?.length > 0 && (
                <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                        {t('product_details.variants', { n: detalle.variantes.length })}
                    </h4>
                    {!esImportado && (
                        <p className="text-xs text-slate-500 mb-2">{t('product_details.choose_variant_hint')}</p>
                    )}
                    <div className="overflow-x-auto max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-700/50 sticky top-0">
                                <tr>
                                    {!esImportado && (
                                        <th className="w-10 px-3 py-2">
                                            <span className="sr-only">{t('product_details.choose_variant')}</span>
                                        </th>
                                    )}
                                    <th className="text-left px-3 py-2 font-medium text-slate-500">
                                        {t('product_details.variant')}
                                    </th>
                                    <th className="text-right px-3 py-2 font-medium text-slate-500">
                                        {t('imported_products.cost')}
                                    </th>
                                    <th className="text-right px-3 py-2 font-medium text-slate-500">
                                        {t('product_details.weight')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {detalle.variantes.map((v, i) => {
                                    const elegida = Boolean(v.vid) && v.vid === vidElegido;
                                    const elegible = !esImportado && Boolean(v.vid);
                                    return (
                                        <tr
                                            key={v.vid || v.sku || i}
                                            onClick={() => elegible && setVidElegido(v.vid)}
                                            className={`border-t border-slate-200 dark:border-slate-700 ${
                                                elegida ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                            } ${elegible ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40' : ''}`}
                                        >
                                            {!esImportado && (
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="radio"
                                                        name="variante"
                                                        checked={elegida}
                                                        onChange={() => setVidElegido(v.vid)}
                                                        disabled={!v.vid}
                                                        aria-label={v.nombre || ''}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{v.nombre || '—'}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-white">
                                                {v.precio !== null ? dinero(v.precio) : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                                                {v.pesoGramos !== null ? `${v.pesoGramos} g` : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(vista.descripcionPropia || detalle?.descripcion) && (
                <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                        {t('product_details.description')}
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed whitespace-pre-line">
                        {vista.descripcionPropia || detalle.descripcion}
                    </p>
                </div>
            )}
        </ModalShell>
    );
}
